import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Star, MapPin } from "lucide-react";

interface FilterSidebarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  category: string;
  onCategoryChange: (value: string) => void;
  categories: string[];
  minRating?: number;
  onMinRatingChange?: (value: number) => void;
  city?: string;
  onCityChange?: (value: string) => void;
  locationType?: string;
  onLocationChange?: (value: string) => void;
  locationOptions?: string[];
  plzSearch?: string;
  onPlzSearchChange?: (value: string) => void;
  idPrefix?: string;
}

export const FilterSidebar = ({ 
  searchTerm, 
  onSearchChange, 
  category, 
  onCategoryChange,
  categories,
  minRating = 0,
  onMinRatingChange,
  city = "all",
  onCityChange,
  locationType = "all",
  onLocationChange,
  locationOptions = [],
  plzSearch = "",
  onPlzSearchChange,
  idPrefix = ""
}: FilterSidebarProps) => {
  const id = (name: string) => idPrefix ? `${idPrefix}-${name}` : name;
  return (
    <div className="bg-card rounded-lg p-6 shadow-md">
      <h2 className="text-xl font-semibold mb-6 text-card-foreground">Filter</h2>
      
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor={id("search")}>Suche</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              id={id("search")} 
              placeholder="Portal suchen..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {onPlzSearchChange && (
          <div className="space-y-2">
            <Label htmlFor={id("plz")}>PLZ / Stadt</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                id={id("plz")} 
                placeholder="PLZ oder Stadt eingeben..."
                className="pl-9"
                value={plzSearch}
                onChange={(e) => onPlzSearchChange(e.target.value)}
              />
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor={id("category")}>Kategorie</Label>
          <Select value={category} onValueChange={onCategoryChange}>
            <SelectTrigger id={id("category")}>
              <SelectValue placeholder="Alle Kategorien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {onCityChange && (
          <div className="space-y-2">
            <Label htmlFor={id("city")}>Stadt</Label>
            <Select value={city} onValueChange={onCityChange}>
              <SelectTrigger id={id("city")}>
                <SelectValue placeholder="Alle Städte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Städte</SelectItem>
                <SelectItem value="Berlin">Berlin</SelectItem>
                <SelectItem value="Hamburg">Hamburg</SelectItem>
                <SelectItem value="München">München</SelectItem>
                <SelectItem value="Frankfurt">Frankfurt</SelectItem>
                <SelectItem value="Köln">Köln</SelectItem>
                <SelectItem value="Düsseldorf">Düsseldorf</SelectItem>
                <SelectItem value="Leipzig">Leipzig</SelectItem>
                <SelectItem value="Stuttgart">Stuttgart</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        
        {onLocationChange && locationOptions.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor={id("location")}>Standort / Region</Label>
            <Select value={locationType} onValueChange={onLocationChange}>
              <SelectTrigger id={id("location")}>
                <SelectValue placeholder="Alle Standorte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Standorte</SelectItem>
                {locationOptions.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {onMinRatingChange && (
          <div className="space-y-2">
            <Label htmlFor={id("rating")}>Mindestbewertung</Label>
            <Select value={minRating.toString()} onValueChange={(value) => onMinRatingChange(Number(value))}>
              <SelectTrigger id={id("rating")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Alle Bewertungen</SelectItem>
                {[5, 4, 3, 2, 1].map((rating) => (
                  <SelectItem key={rating} value={rating.toString()}>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: rating }, (_, i) => (
                        <Star key={i} className="w-3 h-3 fill-primary text-primary" />
                      ))}
                      <span className="ml-1">& mehr</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
};